import React from 'react'
import {
  Button,
  Popover,
  Dialog,
  DialogTrigger,
} from 'react-aria-components'
import { TailwindColor, NeutralColor } from '../../types'
import { COLOR_VALUES } from '../../data/tailwindColors'

interface ColorPickerProps {
  label: string
  value: TailwindColor | NeutralColor | null
  options: readonly (TailwindColor | NeutralColor)[]
  onChange: (color: TailwindColor | NeutralColor | null) => void
  allowNull?: boolean
  compact?: boolean
}

export function ColorPicker({
  label,
  value,
  options,
  onChange,
  allowNull = false,
  compact = false,
}: ColorPickerProps) {
  const displayColor = value ? COLOR_VALUES[value as TailwindColor] : null

  if (compact) {
    return (
      <DialogTrigger>
        <Button
          className="w-8 h-8 rounded-md border border-gray-200 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-1 cursor-pointer transition-all"
          style={{ backgroundColor: displayColor || '#f3f4f6' }}
          title={label + (value ? `: ${value}` : '')}
        >
          {!displayColor && (
            <svg className="w-4 h-4 text-gray-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
        </Button>
        <Popover
          placement="bottom start"
          className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 w-64"
        >
          <Dialog className="outline-none">
            {({ close }) => (
              <div className="space-y-2">
                <div className="text-xs font-medium text-gray-600 mb-2">{label}</div>
                <div className="flex flex-wrap gap-1.5">
                  {allowNull && (
                    <button
                      onClick={() => {
                        onChange(null)
                        close()
                      }}
                      className={`
                        w-7 h-7 rounded-md border-2 transition-all hover:scale-110 flex items-center justify-center cursor-pointer
                        ${value === null ? 'border-gray-900 ring-2 ring-gray-900 ring-offset-1' : 'border-gray-300'}
                      `}
                      style={{ backgroundColor: '#f3f4f6' }}
                      title="None"
                    >
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                  {options.map((color) => (
                    <button
                      key={color}
                      onClick={() => {
                        onChange(color)
                        close()
                      }}
                      className={`
                        w-7 h-7 rounded-md border-2 transition-all hover:scale-110 cursor-pointer
                        ${value === color ? 'border-gray-900 ring-2 ring-gray-900 ring-offset-1' : 'border-transparent hover:border-gray-300'}
                      `}
                      style={{ backgroundColor: COLOR_VALUES[color as TailwindColor] }}
                      title={color}
                    />
                  ))}
                </div>
              </div>
            )}
          </Dialog>
        </Popover>
      </DialogTrigger>
    )
  }

  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-gray-600">{label}</label>
      <DialogTrigger>
        <Button className="w-full flex items-center gap-3 px-3 py-2 text-sm border border-gray-200 rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-left cursor-pointer">
          {displayColor ? (
            <div
              className="w-5 h-5 rounded-md border border-gray-200 flex-shrink-0"
              style={{ backgroundColor: displayColor }}
            />
          ) : (
            <div className="w-5 h-5 rounded-md border border-gray-300 flex-shrink-0 flex items-center justify-center bg-gray-50">
              <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
          )}
          <span className="flex-1 capitalize text-gray-700">
            {value || 'None'}
          </span>
          <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </Button>
        <Popover
          placement="bottom start"
          className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 w-64"
        >
          <Dialog className="outline-none">
            {({ close }) => (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-1.5">
                  {allowNull && (
                    <button
                      onClick={() => {
                        onChange(null)
                        close()
                      }}
                      className={`
                        w-7 h-7 rounded-md border-2 transition-all hover:scale-110 flex items-center justify-center cursor-pointer
                        ${value === null ? 'border-gray-900 ring-2 ring-gray-900 ring-offset-1' : 'border-gray-300'}
                      `}
                      style={{ backgroundColor: '#f3f4f6' }}
                      title="None"
                    >
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                  {options.map((color) => (
                    <button
                      key={color}
                      onClick={() => {
                        onChange(color)
                        close()
                      }}
                      className={`
                        w-7 h-7 rounded-md border-2 transition-all hover:scale-110 cursor-pointer
                        ${value === color ? 'border-gray-900 ring-2 ring-gray-900 ring-offset-1' : 'border-transparent hover:border-gray-300'}
                      `}
                      style={{ backgroundColor: COLOR_VALUES[color as TailwindColor] }}
                      title={color}
                    />
                  ))}
                </div>
              </div>
            )}
          </Dialog>
        </Popover>
      </DialogTrigger>
    </div>
  )
}
